import { jobEntryAny, type DueEntry } from './diary-sets'
import { gutterDayNear, guttersAfterWindows, type CustomerJob } from './jobs'
import { dueDate, missedVisit, pushedNow, rebooked } from './planning'
import { comingUpDays, plannedWeekOf, messagesThisCycle, stageOf, type RebookStage } from './rebook-message'
import type { Customer } from './types'
import { addDaysISO } from './ui'

// Messages for every job (several jobs step 2, PLAN 4a; Liam Q3 "drafted the same way as windows"). Pure, so the
// tests and the paired rehearsal read the rules the screen draws.
//
// Messages builds one row per customer for the main job (job 1, the customer row). This adds a row per RUNNING
// extra job that is due, overdue or planned and not agreed, each read from THAT job's own dates and Messages count
// (GET /api/customer-jobs), through the same stage rules (rebook-message stageOf / messagesThisCycle). A customer
// whose main job and extra job are both waiting for a first message in the same group gets ONE card naming both
// ("Your gutters and windows are due"); that is decided by the screen from `group` below.

export type JobGroup = 'coming_up' | 'overdue' | 'ladder'

export interface JobDue {
  entry: DueEntry // the customer dressed with the job's dates (diary-sets jobEntry)
  job: CustomerJob
  group: JobGroup
  stage: RebookStage
  // A date a person already wrote in the diary for this job in the coming week, not agreed: the message asks
  // them to agree THAT day.
  planned: string | null
  due: string | null
  cold: boolean
}

// The extra jobs Messages lists. `askAgainLater` is the customer's "not now, ask again on…" (followup.ts), which
// quietens their jobs too.
// The look-ahead is the owner's "How far ahead to ask" (a week unless changed), as for the customer rows.
export function jobDues(customers: Customer[], jobs: CustomerJob[] | null | undefined, today: string, askAgainLater: (c: Customer) => boolean, aheadDays: number = comingUpDays()): JobDue[] {
  const byId = new Map(customers.map((c) => [c.id, c]))
  const horizon = addDaysISO(today, aheadDays)
  const out: JobDue[] = []
  for (const j of jobs ?? []) {
    const c = byId.get(j.customer_id)
    if (!c || c.status !== 'active' || askAgainLater(c)) continue
    // Any running job, a one-off too (Astra r1, finding 9): a one-off is never due, but its planned visit still needs
    // agreeing and a missed one following up.
    const e = jobEntryAny(c, j)
    if (!e) continue
    const due = dueDate(e)
    const agreed = !!e.next_confirmed
    const human = !!e.next_booked && e.next_booked_source !== 'suggested' && e.next_booked_source !== 'tentative'
    const week = plannedWeekOf(e)
    const planned = !week && !agreed && human && e.next_booked! >= today && e.next_booked! <= horizon ? e.next_booked! : null
    // Pushed to next week in the Diary's Due (0063): left alone until then, unless a day is already written down.
    if (pushedNow(e, today) && !planned && !week) continue
    const late = !agreed && !!due && due < today && !!e.last_clean
    const missed = missedVisit(e) && !rebooked(e)
    const cold = late && !!e.cycle_days && Math.round((Date.parse(today) - Date.parse(due!)) / 86_400_000) > 2 * e.cycle_days
    const soon = !agreed && !!due && due > today && due <= horizon
    const stage = stageOf(e, today)
    let group: JobGroup | null = null
    if (planned || week && week <= horizon && addDaysISO(week, 6) >= today) group = 'coming_up'
    else if (late || missed) group = 'overdue'
    else if (soon) group = 'coming_up'
    else if (!agreed && messagesThisCycle(e) > 0) group = 'ladder'
    if (!group) continue
    out.push({ entry: e, job: j, group, stage, planned, due, cold })
  }
  return out.sort((a, b) => (a.due ?? '').localeCompare(b.due ?? '') || a.job.id - b.job.id)
}

// The day a message offers for a gutters job (Liam Q11, PLAN 4a): the day the windows are booked when that is
// within a fortnight of the gutters being due (on the windows day, never after); otherwise the round's own pick.
// A planned gutters visit that falls after the windows day (Astra r1, finding 4) is never offered as it is: the
// windows day is, and moving the gutters there needs the owner's yes (plannedAfterWindows says when to ask). A round
// pick that lands after the windows is not offered for gutters either.
export function jobOfferDay(d: Pick<JobDue, 'job' | 'due' | 'planned'>, windowsNext: string | null | undefined, roundPick: string, today: string): string {
  const windows = d.job.kind === 'gutter' && windowsNext && windowsNext >= today ? windowsNext : null
  if (d.planned) return windows && guttersAfterWindows(d.planned, windows) ? windows : d.planned
  if (windows) {
    const near = gutterDayNear(d.due ?? today, windows)
    if (near) return near
    if (roundPick && guttersAfterWindows(roundPick, windows)) return windows
  }
  return roundPick
}

// The gutters are already planned after the windows day: the message may only offer the windows day once the owner
// says yes to moving them. Null when there is nothing to ask.
export function plannedAfterWindows(d: Pick<JobDue, 'job' | 'planned'>, windowsNext: string | null | undefined, today: string): { from: string; to: string } | null {
  if (d.job.kind !== 'gutter' || !d.planned || !windowsNext || windowsNext < today) return null
  return guttersAfterWindows(d.planned, windowsNext) ? { from: d.planned, to: windowsNext } : null
}

// The job's word in the customer's message, lower case: "gutters".
export function jobMessageWord(job: Pick<CustomerJob, 'name'>): string {
  return job.name.trim().toLowerCase() || 'other job'
}
